/**
 * Error handling utilities for Durin AI client
 */

import { ErrorResponse } from '../types';

export class DurinError extends Error {
  public readonly status?: number;
  public readonly response?: ErrorResponse;
  public readonly originalError?: Error;

  constructor(message: string, status?: number, response?: ErrorResponse, originalError?: Error) {
    super(message);
    this.name = 'DurinError';
    if (status !== undefined) this.status = status;
    if (response !== undefined) this.response = response;
    if (originalError !== undefined) this.originalError = originalError;
  }
}

export class DurinNetworkError extends DurinError {
  constructor(message: string, originalError?: Error) {
    super(message, undefined, undefined, originalError);
    this.name = 'DurinNetworkError';
  }
}

export class DurinStreamError extends DurinError {
  constructor(message: string, originalError?: Error) {
    super(message, undefined, undefined, originalError);
    this.name = 'DurinStreamError';
  }
}

export class DurinValidationError extends DurinError {
  constructor(message: string) {
    super(message);
    this.name = 'DurinValidationError';
  }
}

export class DurinRateLimitError extends DurinError {
  public readonly retryAfter?: number | undefined;
  public readonly rule?: string | undefined;

  constructor(message: string, response?: ErrorResponse, retryAfter?: number, rule?: string) {
    super(message, 429, response);
    this.name = 'DurinRateLimitError';
    this.retryAfter = retryAfter;
    this.rule = rule;
  }
}

export function handleHttpError(response: Response, data?: any): DurinError {
  const status = response.status;
  const statusText = response.statusText;

  // Try to extract error message from response
  let message = `HTTP ${status}: ${statusText}`;
  let errorResponse: ErrorResponse | undefined;

  if (data) {
    if (typeof data === 'string') {
      message = data;
    } else if (data.error || data.message) {
      message = data.error || data.message;
      errorResponse = data;
    }
  }

  // Handle 429 rate limiting errors specifically
  if (status === 429) {
    let rateLimitMessage = message;
    let retryAfter: number | undefined;
    let rule: string | undefined;

    if (data && typeof data === 'object') {
      retryAfter = data.retry_after;
      rule = data.rule;

      if (retryAfter) {
        rateLimitMessage = `Rate limit exceeded. You will not be able to make a request for ${retryAfter} seconds due to rate limiting.`;
      } else {
        rateLimitMessage = 'Rate limit exceeded. Please try again later.';
      }
    }

    Actions.showFlash(rateLimitMessage, 'alert');

    return new DurinRateLimitError(rateLimitMessage, errorResponse, retryAfter, rule);
  }

  return new DurinError(message, status, errorResponse);
}

export function isNetworkError(error: Error): boolean {
  return error.name === 'TypeError' && error.message.includes('fetch');
}

export function wrapNetworkError(error: Error): DurinNetworkError {
  return new DurinNetworkError(`Network error: ${error.message}`, error);
}
